import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2, ArrowRight, Clock } from 'lucide-react';
import { SubscriptionPlan, ProductPrice } from '@/hooks/useSubscription';
import PlanFeaturesDisplay from './PlanFeaturesDisplay';
import { useCommunity } from '@/contexts/CommunityContext';

interface PlanCardProps {
  plan: SubscriptionPlan;
  currentPrice?: ProductPrice;
  intervalKey: string;
  isCurrentPlan: boolean;
  isSubscribed: boolean;
  checkoutLoading: boolean;
  portalLoading: boolean;
  onSubscribe: (priceId: string) => void;
  onManageSubscription: () => void;
  communityId: string;
}

const PlanCard = ({
  plan,
  currentPrice,
  intervalKey,
  isCurrentPlan,
  isSubscribed,
  checkoutLoading,
  portalLoading,
  onSubscribe,
  onManageSubscription,
  communityId
}: PlanCardProps) => {
  const { currentCommunity } = useCommunity();

  // Always use Stripe blue for subscription page - professional payment UI standard
  const colors = {
    // Primary CTA - Stripe's trusted blue with gradient
    primary: '#0074d4', // Stripe blue
    primaryHover: '#005bb5', // Darker Stripe blue
    primaryGradient: 'linear-gradient(135deg, #0074d4 0%, #0099ff 100%)', // Professional gradient
    primaryGradientHover: 'linear-gradient(135deg, #005bb5 0%, #0074d4 100%)', // Hover gradient
    
    // Success/Current plan - Professional green
    success: '#10b981', // Emerald-500
    successBg: '#ecfdf5', // Emerald-50
    successBorder: '#6ee7b7', // Emerald-300
    
    // Neutrals - Clean grays
    neutral: '#64748b', // Slate-500
    neutralLight: '#f1f5f9' // Slate-100
  };

  // Helper function to format currency
  const formatCurrency = (amount: number | undefined, currency: string = 'usd') => {
    if (amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toLowerCase(),
      minimumFractionDigits: 2
    }).format(amount / 100);
  };

  // Helper function to get interval display name
  const getIntervalDisplayName = (intervalKey: string) => {
    const [interval, count] = intervalKey.split('-');
    const intervalCount = parseInt(count);
    
    if (intervalCount === 1) {
      switch (interval) {
        case 'month': return 'Monthly';
        case 'year': return 'Yearly';
        case 'week': return 'Weekly';
        case 'day': return 'Daily';
        default: return interval.charAt(0).toUpperCase() + interval.slice(1);
      }
    } else {
      const pluralInterval = interval === 'month' ? 'months' : 
                           interval === 'year' ? 'years' :
                           interval === 'week' ? 'weeks' :
                           interval === 'day' ? 'days' : interval + 's';
      return `Every ${intervalCount} ${pluralInterval}`;
    }
  };

  // Handle inactive plans (Coming Soon)
  if (plan.is_active === false) {
    return (
      <motion.div 
        key={`inactive-${plan.id}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative h-full"
      >
        <Card className="h-full flex flex-col shadow-lg bg-white border border-gray-200 opacity-75">
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <Badge className="bg-gray-500 text-white px-4 py-1 text-sm font-medium">
              Coming Soon
            </Badge>
          </div>
          
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-2xl font-bold text-gray-700">{plan.name}</CardTitle>
            <CardDescription className="text-gray-500 mt-2">
              Advanced features for scaling businesses
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-0 flex-grow">
            {/* Pricing placeholder */}
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-3xl font-bold text-gray-400">
                  Coming Soon
                </span>
              </div>
            </div>
            
            <Separator className="mb-6" />
            
            {/* Features */}
            <PlanFeaturesDisplay planId={plan.id} communityId={communityId} />
            
            {/* Disclaimer */}
            <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center text-yellow-800 text-xs">
                <Clock className="h-4 w-4 mr-2" />
                <span>Features and pricing may change before launch</span>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="pt-6">
            <Button
              disabled
              className="w-full h-12 bg-gray-300 text-gray-500 cursor-not-allowed"
            >
              Coming Soon
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    );
  }

  // Active plan card
  return (
    <motion.div 
      key={`${plan.id}-${intervalKey}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * (plan.rank || 1) }}
      className="relative h-full"
    >
      <Card 
        className={`h-full flex flex-col shadow-lg hover:shadow-xl transition-all duration-300 ${
          isCurrentPlan 
            ? 'border-2 bg-white' 
            : 'border border-gray-200 bg-white hover:border-gray-300'
        }`}
        style={{
          borderColor: isCurrentPlan ? colors.successBorder : undefined,
          backgroundColor: isCurrentPlan ? colors.successBg : undefined
        }}
      >
        {isCurrentPlan && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <Badge 
              className="text-white px-4 py-1 text-sm font-medium border-0"
              style={{ backgroundColor: colors.success }}
            >
              Current Plan
            </Badge>
          </div>
        )}
        
        <CardHeader className="pb-4 text-center">
          <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            {plan.rank === 1 && "Perfect for getting started"}
            {plan.rank === 2 && "Enhanced visibility and features"}
            {plan.rank === 3 && "Maximum impact and growth"}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-0 flex-grow">
          {/* Pricing */}
          {currentPrice && (
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-4xl font-bold text-gray-900">
                  {formatCurrency(currentPrice.amount, currentPrice.currency)}
                </span>
                <span className="text-gray-600">
                  {currentPrice.interval_count === 1 
                    ? `/${currentPrice.interval}` 
                    : `/${getIntervalDisplayName(intervalKey).toLowerCase()}`}
                </span>
              </div>
              {currentPrice.discount_percentage && (
                <div className="mt-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                    {currentPrice.discount_percentage}% OFF
                  </Badge>
                </div>
              )}
            </div>
          )}
          
          <Separator className="mb-6" />
          
          {/* Features */}
          <PlanFeaturesDisplay planId={plan.id} communityId={communityId} />
        </CardContent>
        
        <CardFooter className="pt-6">
          {isCurrentPlan ? (
            <Button
              variant="outline"
              className="w-full h-12 font-semibold transition-colors border-2"
              style={{
                borderColor: colors.success,
                color: colors.success,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.successBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={onManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                "Manage Plan"
              )}
            </Button>
          ) : (
            <Button
              className="w-full h-12 text-white font-semibold transition-all duration-200 shadow-lg hover:shadow-xl border-0"
              style={{
                background: colors.primaryGradient,
                boxShadow: '0 4px 14px 0 rgba(0, 116, 212, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryGradientHover;
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(0, 116, 212, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.primaryGradient;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(0, 116, 212, 0.3)';
              }}
              onClick={() => currentPrice && onSubscribe(currentPrice.id)}
              disabled={checkoutLoading || !currentPrice}
            >
              {checkoutLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <>
                  {isSubscribed ? "Change Plan" : "Subscribe"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default PlanCard;
